import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Loader2,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Percent,
  Hash,
  PieChart as PieChartIcon,
  BarChart3,
  Check,
  MoreHorizontal,
  Download,
} from 'lucide-react';
import { toast } from 'react-toastify';
import useTheme from 'chat-ui/src/hooks/useTheme';
import { CategoryDisplayType, CategorySortType, ECategoryMode } from '@/enums';
import useTranslation from '@/hooks/useTranslation';
import { translateCategories } from '@/pages/projects/single-project/Content/project_results/resultsPageTranslations';
import { useMutateGenerateAiCategories } from '@/reactQuery/post';
import useCategoryStore from '@/store/CategoryStore';
import CogIcon from '../icons/CogIcon';
import SparklesIcon from '../icons/SparklesIcon';
import EditCategoriesModal from '../Modals/EditCategoriesModal';
import { Button } from '../ui/button';
import { Skeleton } from '../ui/skeleton';
import TooltipWrapper from '../tooltip/TooltipWrapper';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Category,
  CategoryDisplay,
  SortOrder,
  CategoryApiResponse,
  SelectedCategory,
} from '@/types';
import { resolveTranslation } from '@/utils/resolveTranslation';
import categoriesColorList from '@/utils/categoriesColorList';
import useDownloadImage from '@/pages/projects/single-project/Content/research_assistant/useDownloadImage';
import CategoriesChart from './CategoriesChart';
import PieChart from './PieChart';

interface CategoriesProps {
  projectId?: string;
  questionID: string;
  questionText: string;
  analysisLanguage: string;
  onSelectCategory: (category: SelectedCategory) => void;
  categories?: CategoryApiResponse;
  isPendingCategories: boolean;
  isPendingInterviewQuestions: boolean;
  showCategoryLoadingIndicator?: boolean;
}

function Categories({
  projectId,
  questionID,
  questionText,
  analysisLanguage,
  onSelectCategory,
  categories,
  isPendingCategories,
  isPendingInterviewQuestions,
  showCategoryLoadingIndicator = false,
}: CategoriesProps) {
  const { lang } = useTranslation();
  const { currentTheme } = useTheme();
  const [openModal, setOpenModal] = useState(false);
  const { categoryType, setCategoryType } = useCategoryStore();
  const [showCategoryDescription, setShowCategoryDescription] = useState<string | null>(null);
  const [sortCategory, setSortCategory] = useState<SortOrder>(CategorySortType.DEFAULT);
  const [categoryDisplay, setCategoryDisplay] = useState<CategoryDisplay>(
    (localStorage.getItem('category_display') as CategoryDisplay) || CategoryDisplayType.TALLY,
  );

  const { mutateAsync: mutateAsyncGenerateAICategories, status: generatingAICategoriesStatus } =
    useMutateGenerateAiCategories();

  // Ref for the chart area container — captures whichever chart is currently
  // shown (bar or pie) when the user triggers "Download PNG".
  const chartAreaRef = useRef<HTMLDivElement>(null);
  const [isDownloadingChart, isDoneChart, triggerChartDownload] = useDownloadImage(chartAreaRef, {
    title: questionText,
    errorMessage: lang.get('msg.downloadFailed'),
  });

  const manualyCreateCategoriesModal = () => {
    setOpenModal(true);
    setCategoryType(ECategoryMode.SINGLE);
  };
  const handleShowCategoryDescription = (id: string) => {
    setShowCategoryDescription((prev) => (prev === id ? null : id));
  };

  const generateAICategories = async () => {
    try {
      await mutateAsyncGenerateAICategories({
        projectID: projectId,
        questionID,
      });
      setOpenModal(true);
    } catch (err) {
      toast.error(lang.get('msg.errorPleaseTryAgain'));
    }
  };

  const calculatePercentages = (data: Category[], convNumber?: number): Category[] | [] => {
    if (!data || !convNumber) return [];
    return data.map((cat: Category) => ({
      ...cat,
      percentage: ((cat.count ?? 0) / convNumber) * 100,
    }));
  };

  const getDataWithPercentagesField = useCallback(calculatePercentages, []);
  const conversationsNumber = categories?.conversations_num;
  const doesCategoryDataExists =
    categories?.categories?.length !== 0 || categories?.categories_multi?.length !== 0;

  const categoriesPicked = useMemo(() => {
    const currentCategories =
      categoryType === ECategoryMode.SINGLE ? categories?.categories : categories?.categories_multi;
    return currentCategories?.length
      ? getDataWithPercentagesField(currentCategories, conversationsNumber)
      : [];
  }, [categories, categoryType, conversationsNumber, getDataWithPercentagesField]);

  const [sortedCategories, setSortedCategories] = useState<Category[]>(categoriesPicked || []);

  const handleSortCategory = useCallback(
    (targetSort: SortOrder) => {
      if (!categoriesPicked) return;
      const sortedData = [...categoriesPicked];
      if (targetSort === CategorySortType.ASC) {
        sortedData.sort((a, b) => (a.count ?? 0) - (b.count ?? 0));
      } else if (targetSort === CategorySortType.DESC) {
        sortedData.sort((a, b) => (b.count ?? 0) - (a.count ?? 0));
      }
      setSortCategory(targetSort);
      setSortedCategories(sortedData);
    },
    [categoriesPicked],
  );

  const isSingleMode = categoryType === ECategoryMode.SINGLE;

  const handleDisplay = () => {
    const next =
      categoryDisplay === CategoryDisplayType.TALLY
        ? CategoryDisplayType.PERCENTAGE
        : CategoryDisplayType.TALLY;
    setCategoryDisplay(next);
    localStorage.setItem('category_display', next);
  };

  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  const handleTogglePie = () => {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    const next = isPie ? CategoryDisplayType.TALLY : CategoryDisplayType.PIE;
    setCategoryDisplay(next);
    localStorage.setItem('category_display', next);
  };

  const getSortIcon = () => {
    if (sortCategory === CategorySortType.ASC) return <ArrowUp className="h-4 w-4" />;
    if (sortCategory === CategorySortType.DESC) return <ArrowDown className="h-4 w-4" />;
    return <ArrowUpDown className="h-4 w-4" />;
  };

  const getPresentationIcon = () => {
    if (categoryDisplay === CategoryDisplayType.TALLY) return <Percent className="h-4 w-4" />;
    return <Hash className="h-4 w-4" />;
  };

  const maxCount =
    categoriesPicked && categoriesPicked.length > 0
      ? Math.max(...categoriesPicked.map((cat) => cat.count ?? 0)) || 1
      : 1;

  // Fall back from pie view when switching to multi-category mode
  useEffect(() => {
    if (categoryType === ECategoryMode.MULTIPLE && categoryDisplay === CategoryDisplayType.PIE) {
      setCategoryDisplay(CategoryDisplayType.TALLY);
      localStorage.setItem('category_display', CategoryDisplayType.TALLY);
    }
  }, [categoryType, categoryDisplay]);

  useEffect(() => {
    if (categories?.last_ran) {
      setCategoryType(categories?.last_ran);
    }
  }, [categories, setCategoryType]);

  useEffect(() => {
    if (categoriesPicked) {
      setSortedCategories(categoriesPicked);
    }
  }, [categoriesPicked]);

  const shouldDisplayCategories = doesCategoryDataExists && !isPendingInterviewQuestions;
  const shouldDisplayModal =
    !isPendingInterviewQuestions && !isPendingCategories && categoriesPicked;
  const shouldDisplaySkeleton = isPendingCategories || !sortedCategories;
  const isPending = generatingAICategoriesStatus === 'pending';
  const isTally = categoryDisplay === CategoryDisplayType.TALLY;
  const isPie = categoryDisplay === CategoryDisplayType.PIE;

  // TELV1-377 — categories always use the neutral pastel
  // `categoriesColorList`. The TELV1-358 sentiment ramp (dark-red →
  // dark-green for rating-scale data) shipped against explicit product
  // direction: charts must not impose a "low = bad, high = good"
  // semantic on numeric scales (a 1-10 noise-level question doesn't
  // make 10 "good"). The pastel palette stays neutral so the chart
  // renders the data without editorial colour weight.
  //
  // Any existing DB-stored category `color` (legacy rainbow hues) is
  // overwritten here so the displayed colour comes from the palette
  // — research-attached connotations don't leak through.
  const displayCategories = useMemo(
    () =>
      sortedCategories.map((cat, i) => ({
        ...cat,
        color: categoriesColorList[i % categoriesColorList.length],
      })),
    [sortedCategories],
  );

  return (
    <div className="flex flex-col">
      {/* edit categories */}
      <div className="flex items-center justify-between w-full mb-3">
        <div className="flex flex-col">
          <h3 style={{ color: currentTheme?.primary }} className="!mb-1 text-lg font-bold">
            {resolveTranslation(translateCategories, analysisLanguage)}
          </h3>
          {shouldDisplayCategories && (
            <p>
              Category mode:
              <b style={{ color: currentTheme?.primary }} className="ml-1 capitalize">
                {categories?.last_ran}
              </b>
            </p>
          )}
        </div>

        {shouldDisplayModal && (
          <EditCategoriesModal
            data={categoriesPicked}
            projectId={projectId}
            questionId={questionID}
            questionText={questionText}
            setOpenModal={setOpenModal}
            openModal={openModal}
            isCategoryDataExists={doesCategoryDataExists}
            conversationsNumber={conversationsNumber}
            isLoading={isPendingCategories}
          />
        )}
      </div>

      {/*  eslint-disable-next-line no-nested-ternary */}
      {shouldDisplaySkeleton ? (
        <div
          style={{ borderColor: currentTheme?.['secondary-text'] }}
          className="flex w-full p-8 border rounded-lg"
        >
          <div className="flex w-full max1100:flex-col max1100:gap-6 max1100:items-center max1100:justify-center">
            {/* skeleton loaders */}
            <div
              style={{ borderColor: currentTheme?.['secondary-text'] }}
              className="flex w-[400px] max1100:w-full max1100:pr-0 items-start border-r pr-3 max1100:border-none"
            >
              <div className="flex flex-col gap-3.5 w-full">
                {[1, 2, 3]?.map((item: number) => (
                  <div key={item} className="flex items-start gap-3">
                    <div className="flex flex-col w-full gap-2">
                      <Skeleton className="w-full h-4 rounded-sm max-w-[50%]" />
                      <Skeleton className="w-full h-3 rounded-sm" />
                      <Skeleton className="w-full h-3 rounded-sm max-w-[90%]" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="items-center justify-center w-full max1100:flex max1100:flex-col">
              <div className="flex justify-end w-full h-full">
                <div className="w-full h-full max1100:flex max1100:mt-5 max1100:items-center max1100:justify-center">
                  <div className="flex flex-col justify-between w-full h-full min-h-full pr-4 ml-12 max1100:max-w-full max1100:gap-4 max1100:ml-0 border-black/30">
                    {[1, 2, 3, 4]?.map((item: number) => (
                      <div key={item} className="flex items-start gap-3">
                        <Skeleton className="w-[90%] rounded-sm h-7" />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : !doesCategoryDataExists ? (
        // buttons to choose category if no category data exists
        <div className="flex flex-col items-center gap-4 mx-auto">
          {/* Polling indicator - show above buttons when generating */}
          {showCategoryLoadingIndicator && (
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>{lang.get('msg.generatingCategories')}...</span>
            </div>
          )}
          <div className="flex items-center gap-4">
            <div className="flex items-center justify-center gap-6">
              <Button disabled={isPending} onClick={generateAICategories} variant="default">
                {isPending ? (
                  <div className="flex items-center gap-1">
                    <Loader2 className="h-5 w-5 animate-spin" />
                    {lang.get('msg.generatingCategories')}
                    ...
                  </div>
                ) : (
                  <div className="flex items-center gap-1">
                    <SparklesIcon className="size-5" />
                    {lang.get('msg.aiSuggestedCategories')}
                  </div>
                )}
              </Button>
            </div>

            <div className="flex items-center justify-center gap-6">
              <Button disabled={isPending} onClick={manualyCreateCategoriesModal} variant="default">
                <CogIcon className="mr-1 size-5" />
                {lang.get('msg.createMyOwnCategories')}
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <div
          style={{ borderColor: currentTheme?.['secondary-text'] }}
          className="p-6 border rounded-lg relative"
        >
          {/* Chart — the ref captures the currently-visible chart for PNG export */}
          <div ref={chartAreaRef}>
            {isPie && isSingleMode ? (
              <div className="flex justify-center">
                <PieChart
                  pieChartNumbers={displayCategories.map((cat) => cat.count ?? 0)}
                  pieChartLabels={displayCategories.map((cat) => cat.label)}
                  pieChartColor={displayCategories.map((cat) => cat.color)}
                />
              </div>
            ) : (
              <CategoriesChart
                fallbackBackgroundColor={currentTheme?.background as string}
                sortedCategories={displayCategories}
                maxCount={maxCount}
                handleShowCategoryDescription={handleShowCategoryDescription}
                showCategoryDescription={showCategoryDescription}
                onSelectCategory={onSelectCategory}
                isTally={isTally}
              />
            )}
          </div>

          <div className="absolute right-4 top-4 flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" type="button">
                  {getSortIcon()}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={() => handleSortCategory(CategorySortType.DEFAULT)}
                  className="flex items-center gap-2"
                >
                  <ArrowUpDown className="h-4 w-4 text-neutral-400" />
                  Default order
                  {sortCategory === CategorySortType.DEFAULT && (
                    <Check className="h-4 w-4 ml-auto text-neutral-600" />
                  )}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => handleSortCategory(CategorySortType.ASC)}
                  className="flex items-center gap-2"
                >
                  <ArrowUp className="h-4 w-4 text-neutral-400" />
                  Count: Low to High
                  {sortCategory === CategorySortType.ASC && (
                    <Check className="h-4 w-4 ml-auto text-neutral-600" />
                  )}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => handleSortCategory(CategorySortType.DESC)}
                  className="flex items-center gap-2"
                >
                  <ArrowDown className="h-4 w-4 text-neutral-400" />
                  Count: High to Low
                  {sortCategory === CategorySortType.DESC && (
                    <Check className="h-4 w-4 ml-auto text-neutral-600" />
                  )}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {!isPie && (
              <TooltipWrapper text={`Display: ${isTally ? 'percentage' : 'tally'}`} className="">
                <Button onClick={handleDisplay} variant="outline" size="sm" type="button">
                  {getPresentationIcon()}
                </Button>
              </TooltipWrapper>
            )}

            {isSingleMode && (
              <TooltipWrapper text={isPie ? 'Bar chart' : 'Pie chart'} className="">
                <Button
                  onClick={handleTogglePie}
                  variant={isPie ? 'default' : 'outline'}
                  size="sm"
                  type="button"
                >
                  {isPie ? <BarChart3 className="h-4 w-4" /> : <PieChartIcon className="h-4 w-4" />}
                </Button>
              </TooltipWrapper>
            )}

            {/* More actions — Download PNG */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  type="button"
                  aria-label={lang.get('msg.downloadChartPng')}
                  className="focus-visible:ring-2 focus-visible:ring-primary/30"
                >
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={triggerChartDownload}
                  disabled={isDownloadingChart}
                  className="flex items-center gap-2"
                >
                  {/* eslint-disable-next-line no-nested-ternary */}
                  {isDownloadingChart ? (
                    <Loader2 className="h-4 w-4 text-neutral-400 animate-spin" />
                  ) : isDoneChart ? (
                    <Check className="h-4 w-4 text-primary" />
                  ) : (
                    <Download className="h-4 w-4 text-neutral-400" />
                  )}
                  {lang.get('msg.downloadChartPng')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      )}
    </div>
  );
}

export default Categories;
